<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRegisterSW } from 'virtual:pwa-register/vue'
import { useI18n } from '../i18n'

const { t } = useI18n()
const online = ref(typeof navigator === 'undefined' ? true : navigator.onLine)
let registration = null
let updateTimer = null

function checkForUpdate() {
  if (online.value && document.visibilityState === 'visible') registration?.update()
}

const { needRefresh, updateServiceWorker } = useRegisterSW({
  immediate: true,
  onRegisteredSW(_url, value) {
    registration = value
    if (registration && updateTimer == null) {
      updateTimer = window.setInterval(checkForUpdate, 60 * 60 * 1000)
    }
  },
  onRegisterError(error) {
    console.warn('PWA registration failed.', error)
  },
})

const isOffline = computed(() => !online.value)
const visible = computed(() => isOffline.value || needRefresh.value)
const title = computed(() => t(isOffline.value ? 'pwaOfflineTitle' : 'pwaUpdateTitle'))
const message = computed(() => t(isOffline.value ? 'pwaOfflineMessage' : 'pwaUpdateMessage'))

function dismiss() {
  needRefresh.value = false
}

function handleOnline() {
  online.value = true
  checkForUpdate()
}

function handleOffline() {
  online.value = false
}

onMounted(() => {
  window.addEventListener('online', handleOnline)
  window.addEventListener('offline', handleOffline)
  document.addEventListener('visibilitychange', checkForUpdate)
})

onBeforeUnmount(() => {
  window.removeEventListener('online', handleOnline)
  window.removeEventListener('offline', handleOffline)
  document.removeEventListener('visibilitychange', checkForUpdate)
  if (updateTimer != null) window.clearInterval(updateTimer)
})
</script>

<template>
  <Transition name="pwa-status">
    <aside v-if="visible" class="pwa-status" :class="{ offline: isOffline }" role="status" aria-live="polite">
      <div>
        <strong>{{ title }}</strong>
        <p>{{ message }}</p>
      </div>
      <div v-if="needRefresh" class="pwa-status-actions">
        <button class="button pwa-update-action" type="button" @click="updateServiceWorker(true)">
          {{ t('pwaUpdateAction') }}
        </button>
        <button class="button button-secondary pwa-dismiss-action" type="button" @click="dismiss">
          {{ t('pwaLaterAction') }}
        </button>
      </div>
    </aside>
  </Transition>
</template>
